import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { basicURL } from "../../apiManager/APIConfig";
import { GetPublicQuestionDetailsCall } from "../../redux/api/question.api";

const isVideoUrl = (url = "") => /\.(mp4|mov|m4v|webm)(\?|$|#)/i.test(String(url));
const isImageUrl = (url = "") => /\.(jpe?g|png|webp|gif)(\?|$|#)/i.test(String(url));

export default function PublicQuestionPage({ questionIdOverride }) {
  const { id: routeId } = useParams();
  const id = questionIdOverride || routeId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [question, setQuestion] = useState(null);
  const [playingVideos, setPlayingVideos] = useState({});
  const [showOpenBanner, setShowOpenBanner] = useState(false);
  const [storeLinks, setStoreLinks] = useState({ ios: "", android: "" });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      const res = await GetPublicQuestionDetailsCall(id);
      if (!mounted) return;
      if (res?.status === 200 && res?.question) {
        setQuestion(res.question);
      } else {
        setError("Question not found or unavailable.");
      }
      setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile) return;
    let mounted = true;

    const loadStoreLinks = async () => {
      try {
        const res = await fetch(`${basicURL}public/public`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data?.data) ? data.data : [];
        const getByKey = (key) => {
          const raw = list.find((x) => x?.key === key)?.data;
          return typeof raw === "string" ? raw.trim() : "";
        };
        if (!mounted) return;
        setStoreLinks({
          ios: getByKey("appstore_link"),
          android: getByKey("googleplay_link"),
        });
      } catch (_) {}
    };
    loadStoreLinks();

    return () => {
      mounted = false;
    };
  }, []);

  const media = useMemo(() => {
    const files = Array.isArray(question?.files) ? question.files.filter((f) => f?.url) : [];
    const hasVideo = files.some((f) => isVideoUrl(f?.url));
    const poster = question?.thumbnail_url || "";
    if (!hasVideo || !poster) return files;
    return files.filter((f) => String(f?.url || "").trim() !== String(poster).trim());
  }, [question]);
  const questionPoster = useMemo(() => {
    if (question?.thumbnail_url) return question.thumbnail_url;
    const files = Array.isArray(question?.files) ? question.files : [];
    const firstImage = files.find((f) => isImageUrl(f?.url));
    return firstImage?.url || "";
  }, [question]);
  const platform = useMemo(() => {
    if (typeof navigator === "undefined") return "other";
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/i.test(ua)) return "ios";
    if (/Android/i.test(ua)) return "android";
    return "other";
  }, []);
  const isMobileDevice = platform === "ios" || platform === "android";
  const installUrl = useMemo(() => {
    if (platform === "ios") return storeLinks.ios || storeLinks.android;
    if (platform === "android") return storeLinks.android || storeLinks.ios;
    return "";
  }, [platform, storeLinks]);
  const appDeepLink = useMemo(
    () => (id ? `microlab://question/${id}` : ""),
    [id]
  );

  useEffect(() => {
    if (!isMobileDevice || !appDeepLink || typeof window === "undefined") return;
    let leftPage = false;
    const onPageHide = () => {
      leftPage = true;
      setShowOpenBanner(false);
    };
    window.addEventListener("pagehide", onPageHide);
    try {
      window.location.assign(appDeepLink);
    } catch (_) {}
    const timer = setTimeout(() => {
      if (!leftPage) setShowOpenBanner(true);
    }, 1200);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [isMobileDevice, appDeepLink]);

  const onPressOpenApp = () => {
    if (!appDeepLink || typeof window === "undefined") return;
    try {
      window.location.assign(appDeepLink);
    } catch (_) {}
    if (installUrl) {
      setTimeout(() => {
        try {
          window.location.assign(installUrl);
        } catch (_) {}
      }, 1400);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(33,150,243,0.18), transparent), radial-gradient(1000px 500px at 90% -20%, rgba(156,39,176,0.18), transparent), #0b1220",
        py: { xs: 5, md: 8 },
      }}
    >
      {isMobileDevice && showOpenBanner && (
        <Box
          sx={{
            position: "fixed",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 16px)",
            maxWidth: 760,
            zIndex: 1300,
          }}
        >
          <Card
            sx={{
              px: 1.2,
              py: 1,
              borderRadius: 2,
              bgcolor: "rgba(9,14,24,0.96)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                <Box
                  component="img"
                  src="/favicon.png"
                  alt="Microlab logo"
                  sx={{ width: 34, height: 34, borderRadius: 1 }}
                />
                <Stack spacing={0} minWidth={0}>
                  <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 700, lineHeight: 1.1 }}>
                    Microlab
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: 12,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    Open in the Microlab app
                  </Typography>
                </Stack>
              </Stack>
              <Button
                onClick={onPressOpenApp}
                variant="contained"
                sx={{
                  minWidth: 74,
                  px: 1.8,
                  py: 0.6,
                  borderRadius: 999,
                  textTransform: "uppercase",
                  fontSize: 12,
                  fontWeight: 800,
                  bgcolor: "#1e88e5",
                  whiteSpace: "nowrap",
                }}
              >
                Open
              </Button>
            </Stack>
          </Card>
        </Box>
      )}
      <Container maxWidth="md">
        <Card
          sx={{
            p: { xs: 2.2, md: 3 },
            mt: { xs: isMobileDevice && !!installUrl ? 7 : 0, md: 0 },
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#fff",
          }}
        >
          {loading ? (
            <Stack alignItems="center" py={7}>
              <CircularProgress />
            </Stack>
          ) : error ? (
            <Typography sx={{ color: "#ffb4b4" }}>{error}</Typography>
          ) : (
            <Stack spacing={2.25}>
              <Typography variant="h5" fontWeight={800} sx={{ color: "#fff" }}>
                Microlab Community
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: "#fff" }}>
                {question?.body || "-"}
              </Typography>

              {media.length > 0 && (
                <Stack spacing={1.5}>
                  {media.map((f) =>
                    isVideoUrl(f?.url) ? (
                      <Box
                        key={f.id || f.url}
                        sx={{
                          position: "relative",
                          width: "100%",
                          borderRadius: 2,
                          overflow: "hidden",
                          background: "#000",
                          minHeight: 260,
                        }}
                      >
                        {!playingVideos[f.id || f.url] ? (
                          <Box
                            onClick={() =>
                              setPlayingVideos((prev) => ({
                                ...prev,
                                [f.id || f.url]: true,
                              }))
                            }
                            sx={{
                              position: "relative",
                              width: "100%",
                              minHeight: 260,
                              cursor: "pointer",
                            }}
                          >
                            {questionPoster ? (
                              <Box
                                component="img"
                                src={questionPoster}
                                alt="video thumbnail"
                                sx={{
                                  width: "100%",
                                  minHeight: 260,
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  minHeight: 260,
                                  bgcolor: "rgba(0,0,0,0.45)",
                                }}
                              />
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.22)",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 74,
                                  height: 74,
                                  borderRadius: "50%",
                                  bgcolor: "#1e88e5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                                }}
                              >
                                <PlayArrowRoundedIcon sx={{ color: "#fff", fontSize: 44 }} />
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            component="video"
                            controls
                            playsInline
                            preload="metadata"
                            src={f.url}
                            poster={questionPoster || undefined}
                            sx={{ width: "100%", display: "block", background: "#000" }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        key={f.id || f.url}
                        component="img"
                        src={f.url}
                        alt="question media"
                        sx={{ width: "100%", borderRadius: 2, display: "block" }}
                      />
                    )
                  )}
                </Stack>
              )}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.18)" }} />
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.88)" }}>
                Comments ({question?.comments?.length || 0})
              </Typography>
              <Stack spacing={1.25}>
                {(question?.comments || []).map((c) => (
                  <Card
                    key={c.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: "rgba(255,255,255,0.03)",
                      borderColor: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                      {c?.user?.first_name || "User"} {c?.user?.last_name || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {c?.comment}
                    </Typography>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Box>
  );
}
